import './registerForm.scoped.scss'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { FormikHelpers, useFormik } from "formik"
import * as yup from "yup"
import { toast } from "react-hot-toast"
import { useSicalcApiService } from "services/sicalcApi/sicalcApiService"
import { getServerErrorMessage } from "services/sicalcApi/errors/getServerErrorMessage"
import { TextField } from "components/common/formFields/textField/textField"
import {
  SearchableMultiSelectField,
} from "components/common/formFields/searchableMultiSelectField/searchableMultiSelectField"
import {
  CompanyField,
  CompanyFieldValues,
  companyFieldValidation,
  initialCompanyFieldValues,
  useCompanies,
} from "components/common/formFields/companyField/companyField"
import { getFieldErrorHelpers, ServerFieldError } from "utils/formFieldErrors"
import { getSicalcUserRoleLabel } from "services/auth/models/sicalcUserRole"
import { CheckboxInput } from "components/common/formFields/CheckboxInput/CheckboxInput"
import { PrimaryButton } from "components/common/buttons/primaryButton/primaryButton"
import { LightButton } from "components/common/buttons/lightButton/lightButton"

export interface RegisterFormProps {
  onRegistered: () => void
  onCancel: () => void
}

interface FormDataModel extends CompanyFieldValues {
  firstName: string
  lastName: string
  email: string
  password: string
  requestedRoles: string[]
}

// Matches the limits on the server's request models
const maxLength = 250
const tooLongMessage = `For langt (maks ${maxLength} tegn)`

const requiredText = yup.string().trim().required("Påkrevd").max(maxLength, tooLongMessage)

const validationSchema = yup.object().shape({
  ...companyFieldValidation,
  firstName: requiredText,
  lastName: requiredText,
  email: yup.string()
    .trim()
    .required("Påkrevd")
    .email("Må være en e-postadresse")
    .max(maxLength, tooLongMessage),
  password: yup.string()
    .required("Påkrevd")
    .min(8, "Må bestå av minst 8 tegn")
    .max(maxLength, tooLongMessage),
  requestedRoles: yup.array()
    .of(yup.string())
    .min(1, "Velg minst én rolle"),
})

const initialValues: FormDataModel = {
  ...initialCompanyFieldValues,
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  requestedRoles: [],
}

export const RegisterForm = (props: RegisterFormProps) => {
  const { authenticationApi } = useSicalcApiService()
  const { companies, createCompany } = useCompanies()

  const [ roles, setRoles ] = useState<string[]>()
  const [ showPassword, setShowPassword ] = useState(false)
  const [ submissionError, setSubmissionError ] = useState<string>()
  const [ serverFieldErrors, setServerFieldErrors ] = useState<ServerFieldError<FormDataModel>[]>([])

  const getRoles = useCallback(async () => {
    try {
      return await authenticationApi.getValidRoles()
    } catch {
      toast.error("Kunne ikke hente rollene")
      return []
    }
  }, [authenticationApi])

  useEffect(() => {
    // Ignore a response that arrives after the form is gone or a newer request started
    let ignore = false
    getRoles().then(roles => {
      if (!ignore) {
        setRoles(roles)
      }
    })
    return () => {
      ignore = true
    }
  }, [getRoles])

  // Shown in Norwegian, but the role names are what the server expects
  const roleOptions = useMemo(() => roles?.map(role => ({
    label: getSicalcUserRoleLabel(role),
    value: role,
  })), [roles])

  const handleSubmission = async (values: FormDataModel, helpers: FormikHelpers<FormDataModel>) => {
    setSubmissionError(undefined)
    setServerFieldErrors([])
    let companyId = values.companyId

    if (values.isNewCompany) {
      const result = await createCompany(values)
      if ("conflicts" in result) {
        setServerFieldErrors(result.conflicts)
        return
      }
      if ("errorMessage" in result) {
        setSubmissionError(result.errorMessage)
        return
      }
      companyId = result.companyId

      // The company exists from now on, so trying again must not create it a second time
      await helpers.setValues({
        ...values,
        isNewCompany: false,
        companyId,
      }, false)
    }

    const email = values.email.trim().toLowerCase()

    try {
      await authenticationApi.createUser({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        companyId,
        // The sign in form asks for the e-mail address as the username
        userName: email,
        email,
        password: values.password,
        requestedRoles: values.requestedRoles.join(","),
      })
    } catch (error) {
      const message = getServerErrorMessage(error) ?? "Kunne ikke registrere brukeren"
      setSubmissionError(values.isNewCompany
        ? `Firmaet ble opprettet, men brukeren ble ikke registrert: ${message}`
        : message)
      return
    }

    props.onRegistered()
  }

  const form = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmission,
  })

  const { isInvalid, errorsFor } = getFieldErrorHelpers(form, serverFieldErrors)

  return (
    <form onSubmit={form.handleSubmit} className="register-form column gap4" noValidate>
      <div className="row gap3">
        <div className="grow">
          <TextField
            label="Fornavn*"
            name="firstName"
            autoComplete="given-name"
            autoFocus
            value={form.values.firstName}
            isInvalid={isInvalid("firstName")}
            errors={errorsFor("firstName")}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
        </div>
        <div className="grow">
          <TextField
            label="Etternavn*"
            name="lastName"
            autoComplete="family-name"
            value={form.values.lastName}
            isInvalid={isInvalid("lastName")}
            errors={errorsFor("lastName")}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
        </div>
      </div>

      <CompanyField
        label="Firma*"
        form={form}
        companies={companies}
        isInvalid={isInvalid}
        errorsFor={errorsFor}
      />

      <TextField
        label="E-post*"
        name="email"
        type="email"
        autoComplete="email"
        value={form.values.email}
        isInvalid={isInvalid("email")}
        errors={errorsFor("email")}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
      />

      <div className="column gap2">
        <TextField
          label="Passord*"
          name="password"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          value={form.values.password}
          isInvalid={isInvalid("password")}
          errors={errorsFor("password")}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
        />
        <CheckboxInput label="Vis passord" value={showPassword} onChange={setShowPassword} />
      </div>

      <div className="column gap1">
        <label className="column">
          <span className="form-label">Rolle*</span>
          <SearchableMultiSelectField
            options={roleOptions}
            isLoading={roles === undefined}
            values={form.values.requestedRoles}
            isInvalid={isInvalid("requestedRoles")}
            onChange={values => form.setFieldValue("requestedRoles", values.map(String))}
            onBlur={() => form.setFieldTouched("requestedRoles", true)}
          />
        </label>
        {isInvalid("requestedRoles") && (
          <div className="validation-error">{errorsFor("requestedRoles")}</div>
        )}
      </div>

      {submissionError && (
        <div className="validation-error" role="alert">{submissionError}</div>
      )}

      <div className="controls column gap2">
        <PrimaryButton submit disabled={form.isSubmitting}>
          {form.isSubmitting ? "Registrerer ..." : "Registrer"}
        </PrimaryButton>
        <LightButton onClick={props.onCancel}>Tilbake til innlogging</LightButton>
      </div>
    </form>
  )
}
